import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation } from 'react-router-dom';
import RagClientSelect, { formatClientLabel } from './RagClientSelect';
import AssistantMessage from './AssistantMessage';
import AssistantSlashMenu from './AssistantSlashMenu';
import { AppPillSwitch } from '../ui';
import useAssistantChat, { ASSISTANT_MODES } from '../../hooks/useAssistantChat';
import { useAssistantPageClient } from '../../context/AssistantPageClientContext';
import { matchRagClient } from '../../utils/ragClientMatch';
import menuVisibilityService from '../../services/menuVisibilityService';
import { ROUTES } from '../../config/routes';
import { AppAlert, AppButton } from '../ui';
import '../../styles/assistant-widget.scss';
import '../../styles/assistant-slash.scss';
import '../../styles/kkp-planning-view-mode.scss';

const MODE_OPTIONS = [
  {
    value: ASSISTANT_MODES.question,
    label: 'Question',
    icon: 'mdi:chat-question-outline',
    hint: 'Poser une question sur les documents indexés',
  },
  {
    value: ASSISTANT_MODES.synthese,
    label: 'Synthèse',
    icon: 'mdi:file-document-outline',
    hint: 'Générer une synthèse pour le client sélectionné',
  },
];

export default function AssistantChatWidget() {
  const location = useLocation();
  const { pageClientLabel } = useAssistantPageClient();
  const [open, setOpen] = useState(false);
  const [menuTick, setMenuTick] = useState(0);

  const chat = useAssistantChat();
  const {
    clients,
    clientsLoading,
    clientsError,
    selectedClient,
    setSelectedClient,
    syncFromPageMatch,
    mode,
    setMode,
    messages,
    input,
    setInput,
    sending,
    examples,
    syntheseDisabled,
    textareaRef,
    messagesEndRef,
    sendQuestion,
    handleRetry,
    slashOpen,
    slashItems,
    slashIndex,
    setSlashIndex,
    selectSlashSkill,
    handleComposerKeyDown,
  } = chat;

  useEffect(() => {
    let cancelled = false;
    menuVisibilityService.load()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMenuTick((t) => t + 1);
      });
    return () => { cancelled = true; };
  }, []);

  const canSee = menuVisibilityService.canSee('page.assistant');
  const isAssistantRoute = location.pathname.startsWith(ROUTES.assistant);
  const visible = canSee && !isAssistantRoute;

  const matched = useMemo(
    () => matchRagClient(pageClientLabel, clients),
    [pageClientLabel, clients],
  );

  useEffect(() => {
    if (!visible || clientsLoading) {
      return;
    }
    syncFromPageMatch(matched?.slug || '');
  }, [visible, clientsLoading, matched?.slug, syncFromPageMatch]);

  // menuTick force re-eval après chargement des droits
  void menuTick;

  if (!visible) {
    return null;
  }

  const fullscreenHref = selectedClient
    ? `${ROUTES.assistant}?client=${encodeURIComponent(selectedClient)}`
    : ROUTES.assistant;

  const detectedLabel = matched
    ? formatClientLabel(matched.slug)
    : null;

  const handleSubmit = (event) => {
    event.preventDefault();
    sendQuestion(input);
  };

  return (
    <div className="assistant-widget">
      {open && (
        <div className="assistant-widget__panel" role="dialog" aria-label="Assistant itiConseil">
          <div className="assistant-widget__header">
            <div className="assistant-widget__header-title">
              <Icon icon="mdi:robot-outline" className="text-primary-600" />
              <span>Assistant itiConseil</span>
            </div>
            <div className="assistant-widget__header-actions">
              <Link
                to={fullscreenHref}
                className="btn btn-sm btn-outline-secondary"
                title="Ouvrir en plein écran"
                onClick={() => setOpen(false)}
              >
                <Icon icon="mdi:fullscreen" />
              </Link>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-label="Fermer"
                onClick={() => setOpen(false)}
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <div className="assistant-widget__toolbar">
            {clientsError && (
              <AppAlert variant="warning" className="py-8 px-12 mb-8 text-sm">{clientsError}</AppAlert>
            )}
            <RagClientSelect
              clients={clients}
              value={selectedClient}
              onChange={setSelectedClient}
              loading={clientsLoading}
              disabled={sending}
            />
            {detectedLabel && selectedClient === matched?.slug && (
              <p className="assistant-widget__detected text-secondary-light text-xs mb-0 mt-4">
                Client détecté : {detectedLabel}
              </p>
            )}
            <div className="mt-10 d-flex justify-content-start">
              <AppPillSwitch
                ariaLabel="Mode assistant"
                value={mode}
                disabled={sending}
                onChange={setMode}
                options={MODE_OPTIONS.map((option) => (
                  option.value === ASSISTANT_MODES.synthese
                    ? {
                        ...option,
                        disabled: syntheseDisabled,
                        hint: syntheseDisabled
                          ? 'Sélectionnez un client pour générer une synthèse'
                          : option.hint,
                      }
                    : option
                ))}
              />
            </div>
          </div>

          <div className="assistant-widget__messages">
            {messages.length === 0 ? (
              <div className="assistant-widget__welcome text-center">
                <div className="assistant-widget__welcome-icon mb-10">
                  <Icon icon="mdi:robot-outline" className="text-primary-600" />
                </div>
                <p className="text-secondary-light text-sm mb-12">
                  Posez une question, ou tapez / pour un skill.
                </p>
                <div className="d-flex flex-column gap-8">
                  {examples.slice(0, 2).map((example) => (
                    <button
                      key={example}
                      type="button"
                      className="btn btn-outline-secondary btn-sm text-start"
                      onClick={() => sendQuestion(example)}
                      disabled={sending}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="assistant-widget__thread">
                {messages.map((message) => (
                  <AssistantMessage
                    key={message.id}
                    message={message}
                    onRetry={message.error ? handleRetry : undefined}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <form className="assistant-widget__composer" onSubmit={handleSubmit}>
            <AssistantSlashMenu
              open={slashOpen}
              items={slashItems}
              activeIndex={slashIndex}
              onHover={setSlashIndex}
              onSelect={selectSlashSkill}
            />
            <textarea
              ref={textareaRef}
              className="form-control assistant-widget__textarea"
              rows={1}
              placeholder="Question ou /skill…"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              disabled={sending}
            />
            <AppButton
              type="submit"
              variant="primary"
              className="assistant-widget__send"
              disabled={sending || !input.trim()}
              loading={sending}
              icon="mdi:send"
              aria-label="Envoyer"
            />
          </form>
        </div>
      )}

      <button
        type="button"
        className={`assistant-widget__fab ${open ? 'assistant-widget__fab--open' : ''}`}
        aria-label={open ? 'Fermer l’assistant' : 'Ouvrir l’assistant itiConseil'}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Icon icon={open ? 'mdi:close' : 'mdi:robot-outline'} />
      </button>
    </div>
  );
}
